const presignedUrlCache = new Map();

export const isS3Url = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('s3.') || url.includes('s3.amazonaws.com') || url.includes('.s3.');
};

export const isAzureBlobUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('blob.core.windows.net');
};

const extractS3Key = (url) => {
    try {
      const urlObj = new URL(url);
  
      let bucket = "";
      let key = "";
  
      // Virtual-hosted style: https://bucket.s3.region.amazonaws.com/key
      if (urlObj.hostname.includes(".s3.")) {
        bucket = urlObj.hostname.split(".s3.")[0];
        key = urlObj.pathname.substring(1); 
      }
  
      // Path-style: https://s3.region.amazonaws.com/bucket/key
      else if (urlObj.hostname.startsWith("s3.")) {
        const parts = urlObj.pathname.split("/").filter(Boolean);
        bucket = parts[0];
        key = parts.slice(1).join("/");
      }
  
      if (!bucket || !key) return null;
  
      // ⭐ Decode %20, %2520, etc. → EXACTLY what BlobClient does
      key = decodeURIComponent(key);
  
      return { bucket, key };
    } catch (err) {
      console.error("extractS3Key error:", err);
      return null;
    }
  };
  

  const extractBucketAndKeyFromBlobUrl = (blobUrl) => {
    try {
      const urlObj = new URL(blobUrl);
  
      if (urlObj.hostname.includes('blob.core.windows.net')) {
        const pathParts = urlObj.pathname.split('/').filter(p => p);
  
        if (pathParts.length >= 2) {
          let bucket = pathParts[0];
          const key = decodeURIComponent(pathParts.slice(1).join('/'));
  
          // ⭐ Ensure bucket starts with "fink-"
          if (!bucket.startsWith("fink-")) {
            bucket = "fink-" + bucket;
          }
  
          return { bucket, key };
        }
      }
  
      return null;
    } catch (error) {
      console.error('Error extracting bucket and key from blob URL:', error);
      return null;
    }
  };
  

const getS3UrlFromBlobUrl = async (blobUrl) => {
  try {
    console.log('🔄 Extracting bucket and key from blob URL:', blobUrl);
    
    const s3Info = extractBucketAndKeyFromBlobUrl(blobUrl);
    if (!s3Info) {
      console.warn('❌ Could not extract bucket and key from blob URL:', blobUrl);
      return null;
    }

    const endpoints = [
      'https://stage-apiplatform.finkraftai.com/api/fuzzy/presignedUrl',
    ];

    for (const endpoint of endpoints) {
      try {
        console.log('📡 Calling API with bucket and key:', endpoint, s3Info);

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ResponseContentDisposition': 'inline'
          },
          body: JSON.stringify({
            bucket: s3Info.bucket,
            key: s3Info.key,
            originalUrl: blobUrl,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          
          if (result.status && result.data?.presignedUrl) {
            return result.data.presignedUrl;
          }
          
          if (result.presignedUrl) {
            return result.presignedUrl;
          }

          if (result.status && result.data?.s3Url) {
            return result.data.s3Url;
          }
          
          if (result.s3Url) {
            return result.s3Url;
          }
        } else {
          const errorText = await response.text();
          console.error('❌ API call failed:', response.status, errorText);
        }
      } catch (endpointError) {
        console.error('❌ Error calling endpoint:', endpoint, endpointError);
        continue;
      }
    }

    console.warn('⚠️ Could not get pre-signed URL from blob URL');
    return null;
  } catch (error) {
    console.error('❌ Error getting pre-signed URL from blob URL:', error);
    return null;
  }
};

const getPresignedUrlFromBackend = async (s3Url) => {
  try {
    console.log('🔍 Getting pre-signed URL for:', s3Url);
    const s3Info = extractS3Key(s3Url);
    if (!s3Info) {
      console.warn('❌ Could not extract S3 key from URL:', s3Url);
      return s3Url;
    }

    console.log('✅ Extracted S3 info:', s3Info);

    const endpoints = [
      'https://stage-apiplatform.finkraftai.com/api/fuzzy/presignedUrl',
    ];

    for (const endpoint of endpoints) {
      try {
        console.log('📡 Calling API:', endpoint, 'with payload:', {
          bucket: s3Info.bucket,
          key: s3Info.key,
          originalUrl: s3Url,
        });

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ResponseContentDisposition': 'inline'
          },
          body: JSON.stringify({
            bucket: s3Info.bucket,
            key: s3Info.key,
            originalUrl: s3Url,
          }),
        });


        if (response.ok) {
          const result = await response.json();
          console.log('📦 Response data:', result);
          
          if (result.status && result.data?.presignedUrl) {
            console.log('✅ Got pre-signed URL from result.data.presignedUrl');
            return result.data.presignedUrl;
          }
          
          if (result.presignedUrl) {
            console.log('✅ Got pre-signed URL from result.presignedUrl');
            return result.presignedUrl;
          }
          
          if (result.data?.url) {
            console.log('✅ Got pre-signed URL from result.data.url');
            return result.data.url;
          }

          console.warn('⚠️ Response OK but no presignedUrl found in result:', result);
        } else {
          const errorText = await response.text();
          console.error('❌ API call failed:', response.status, errorText);
        }
      } catch (endpointError) {
        console.error('❌ Error calling endpoint:', endpoint, endpointError);
        continue;
      }
    }

    console.warn('⚠️ Failed to get pre-signed URL from any endpoint, using original URL. Please ensure backend API endpoint exists.');
    return s3Url;
  } catch (error) {
    console.error('❌ Error getting pre-signed URL:', error);
    return s3Url;
  }
};

export const getPresignedUrl = async (url, forceRefresh = false) => {
  console.log('🚀 getPresignedUrl called with:', url, 'forceRefresh:', forceRefresh);
  
  if (!url || typeof url !== 'string') {
    console.log('⚠️ Invalid URL, returning as-is');
    return url;
  }

  let finalUrl = url;

  if (isAzureBlobUrl(url)) {
    console.log('🔵 Detected Azure Blob URL, extracting bucket and key...');
    const presignedUrl = await getS3UrlFromBlobUrl(url);
    if (presignedUrl) {
      console.log('✅ Got pre-signed URL from blob URL:', presignedUrl);
      return presignedUrl;
    } else {
      console.warn('⚠️ Could not get pre-signed URL from blob URL, using original');
      return url;
    }
  }

  const isS3 = isS3Url(finalUrl);
  console.log('🔍 Is S3 URL?', isS3);
  
  if (!isS3) {
    console.log('⚠️ Not an S3 URL, returning as-is');
    return finalUrl;
  }

  const cacheKey = url;
  if (!forceRefresh && presignedUrlCache.has(cacheKey)) {
    const cached = presignedUrlCache.get(cacheKey);
    if (Date.now() - cached.timestamp < 50 * 60 * 1000) {
      console.log('✅ Using cached pre-signed URL');
      return cached.url;
    }
    console.log('⏰ Cached URL expired, fetching new one');
  }

  console.log('📞 Calling backend to get pre-signed URL...');
  const presignedUrl = await getPresignedUrlFromBackend(finalUrl);
  
  presignedUrlCache.set(cacheKey, {
    url: presignedUrl,
    timestamp: Date.now(),
  });

  console.log('✅ Returning pre-signed URL:', presignedUrl);
  return presignedUrl;
};

export const clearPresignedUrlCache = () => {
  presignedUrlCache.clear();
};
